import isClient from './util/detectClient';
import request from './util/request';
import configurable from './util/configurable';
import {
    Config,
    HB_SESSION_ID_KEY
} from './util/config';
import cookie from './util/cookie';
import {camelToUnder} from './util/json';

// 上报字段设计文档
// https://doc.baijia.com/sheets/qeK4Xdxva72RSLWM/y7NM8


const HB_SESSION_ID_VALUE = cookie.get(HB_SESSION_ID_KEY);

/**
 * 业务手动上报日志
 *
 * @function
 * @param {Object} json 要上报的数据
 * @param {Function=} callback 上报后的回调
 */

const report = configurable((json, callback) => {
    if (!isClient) {
        return;
    }

    // 多取几次appNid是为了兼容历史传参情况
    const appNid = Config.APPNID || Config.appNid || Config.appId;
    const {type, level} = json;
    if (appNid && type && level) {

        Object.assign(json, {
            [HB_SESSION_ID_KEY]: HB_SESSION_ID_VALUE || cookie.get(HB_SESSION_ID_KEY)
        });

        const reportData = {
            actionStart: Date.now(),
            appNid,
            reqType: 'log',
            ...json
        };

        request(Config.VERB)(`${Config.LOG}`, camelToUnder(reportData), callback);
    }
}, {});


class Logger {
    constructor() {
        this.type = 'NORMAL';
        this.level = 'NORMAL';

        /**
         * 日志级别 level
         * 0-'NORMAL',  普通
         * 1-'URGENT',  紧急
         * 2-'CRITICAL', 严重
         * 3-'MINIMAL', 轻微
         */
        this.levelList = [
            'NORMAL',
            'URGENT',
            'CRITICAL',
            'MINIMAL',
        ];

        /**
         * 类型映射
         * type
         * 0-NORMAL 普通日志
         * 1-STATIC_RESOURCE 静态资源
         * 2-JAVASCRIPT js执行
         * 3-HTTP_REQUEST 接口请求
         * 4-PAGE_BLANK 白屏
         * 5-BUSINESS 业务自定义
         */
        this.typeList = [
            'NORMAL',
            'STATIC_RESOURCE',
            'JAVASCRIPT',
            'HTTP_REQUEST',
            'PAGE_BLANK',
            'BUSINESS'
        ];
    }

    /**
     * 设置日志级别
     *
     * @param {string} levelDesc 日志级别
     */
    setLevel(levelDesc = 'NORMAL') {
        this.level = `${this.levelList.findIndex(level => levelDesc === level)}`;
    }

    /**
     * 设置日志类型
     *
     * @param {string} typeDesc 日志类型
     */
    setType(typeDesc = 'NORMAL') {
        this.type = `${this.typeList.findIndex(type => typeDesc === type)}`;
    }

    /**
     * 自己设置上报日志
     *
     * @param {Object} info 日志相关参数
     * @param {string} info.level 日志级别
     * @param {string} info.type 日志类型
     * @param {string} info.description 日志描述信息
     * @param {Object} info.extra 日志补充信息
     */
    log(info) {
        const {level = 'NORMAL', type = 'NORMAL', description = '', extra = {}} = info;

        this.setLevel(level);
        this.setType(type);

        if (this.isLogValid(info)) {
            report({
                level: this.level,
                type: this.type,
                description: JSON.stringify(description),
                extra: JSON.stringify(extra)
            });
        }
    }

    /**
     * 发送 NORMAL 级别的日志
     *
     * @param {Object} params 日志参数
     */
    info(params) {
        this.setLevel('NORMAL');
        this.sendReport(params);
    }

    /**
     * 发送 MINIMAL 级别的日志
     *
     * @param {Object} params 日志参数
     */
    warn(params) {
        this.setLevel('MINIMAL');
        this.sendReport(params);
    }

    /**
     * 发送 CRITICAL 级别的日志
     *
     * @param {Object} params 日志参数
     */
    error(params) {
        this.setLevel('CRITICAL');
        this.sendReport(params);
    }

    /**
     * 发送 URGENT 级别的日志
     *
     * @param {Object} params 日志参数
     */
    fatal(params) {
        this.setLevel('URGENT');
        this.sendReport(params);
    }

    /**
     * 检查日志level type 是否可以满足发送要求
     *
     * @param {Object} params 日志配置参数
     */
    isLogValid(params) {
        const errTitle = '【哈勃】【log】参数有误，可能导致无法正常上报日志\r\n';
        if (+this.level < 0) {
            console.error(
                errTitle,
                '参数中的 level 为：',
                params.level,
                '\r\n',
                `预期的 level 为 ${this.levelList.join(',')} 其中之一\r\n`
            );
            return false;
        }

        if (+this.type < 0) {
            console.error(
                errTitle,
                '参数中的 type 为：',
                params.type,
                '\r\n',
                `预期的 type 为 ${this.typeList.join(',')} 其中之一\r\n`
            );
            return false;
        }
        return true;
    }


    /**
     * 发送日志,用于提前设置好 level 的场景
     *
     * @param {Object} params 日志参数
     * @private
     */
    sendReport(params) {
        const {type = '', description = '', extra = {}} = params;
        type && this.setType(type);

        if (this.isLogValid()) {
            report({
                description: JSON.stringify(description),
                extra: JSON.stringify(extra),
                type: this.type,
                level: this.level,
            });
        }
    }
}

export default Logger;
